import * as R from "ramda";
import { format } from "date-fns";
import { getCurveName, getGranName } from "../settlement";
const expandHourlyRowToQuarters = (row: any) => {
  const d = new Date(row.dateTimeOffset);
  if (d.getMinutes() !== 0) return [row]; // già quartoraria
  const baseVal = typeof row.value === "number" ? row.value : 0;
  return [0, 15, 30, 45].map((m) => {
    const q = new Date(d);
    q.setMinutes(m, 0, 0);
    return {
      ...row,
      dateTimeOffset: q.toISOString(),
      value: baseVal / 4,
    };
  });
};

/// NUOVA LOGICA: se il gruppo orario ha UNA sola riga minuto 0 -> sempre espansione.
// Altrimenti manteniamo le righe così come arrivano (già quarti o parziali).
// tideDate rimossa dall’equazione.
const getQuarterlyInfo = (rows: any[]) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const sorted = [...rows].sort(
    (a, b) =>
      new Date(a.dateTimeOffset).getTime() -
      new Date(b.dateTimeOffset).getTime()
  );

  // Gruppo per curveName + ora tronca
  const groups: Record<string, any[]> = {};
  for (const r of sorted) {
    const d = new Date(r.dateTimeOffset);
    const hourStart = new Date(d);
    hourStart.setMinutes(0, 0, 0);
    const key = `${r.curveName}__${hourStart.getTime()}`;
    (groups[key] = groups[key] || []).push(r);
  }

  const out: any[] = [];
  for (const [key, group] of Object.entries(groups)) {
    const [curveName] = key.split("__");
    const minutesSet = new Set(
      group.map((g) => new Date(g.dateTimeOffset).getMinutes())
    );
    const isPureHourly =
      group.length === 1 && minutesSet.size === 1 && minutesSet.has(0);

    if (isPureHourly) {
      out.push(
        ...expandHourlyRowToQuarters(group[0]).map((q) => ({
          ...q,
          curveName,
          Date: q.dateTimeOffset,
        }))
      );
    } else {
      out.push(
        ...group.map((g) => ({
          ...g,
          curveName,
          Date: g.dateTimeOffset,
        }))
      );
    }
  }

  out.sort(
    (a, b) =>
      new Date(a.Date).getTime() - new Date(b.Date).getTime() ||
      a.curveName.localeCompare(b.curveName)
  );
  return out;
};
const getQuarterlyDates = (rows: any[]) => {
  const info = getQuarterlyInfo(rows);
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const r of info) {
    if (!seen.has(r.Date)) {
      seen.add(r.Date);
      dates.push(r.Date);
    }
  }
  return dates;
};
const getDailyDates = R.pipe<any, any, any>(
  R.map((row: any) => format(new Date(row.dateTimeOffset), "dd-MM-yyyy")),
  R.uniq
);
const getHourlyDates = (rows: any[]) => {
  const hourly = getHourlyInfo(rows);
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const r of hourly) {
    if (!seen.has(r.Date)) {
      seen.add(r.Date);
      dates.push(r.Date);
    }
  }
  return dates;
};

const getDailyInfo = R.pipe<any, any, any, any, any>(
  R.map((row: any) =>
    R.assoc("Date", format(new Date(row.dateTimeOffset), "dd-MM-yyyy"), row)
  ),
  R.groupBy((x: any) => `${x.Date}-${x.curveName}`),
  R.map((x: any) => {
    const val = x.reduce(
      (a: any, b: any) => ({
        ...b,
        value: a.value + R.pathOr(0, ["value"], b),
      }),
      {
        value: 0,
      }
    );
    return {
      ...val,
      value: val.value,
    };
  }),
  R.values
);
const getHourlyInfo = (rows: any[]) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const groups: Record<string, any[]> = {};
  for (const row of rows) {
    const d = new Date(row.dateTimeOffset);
    const hour = new Date(d);
    hour.setMinutes(0, 0, 0);
    const key = `${row.curveName}__${hour.getTime()}`;
    (groups[key] = groups[key] || []).push(row);
  }
  const out: any[] = [];
  for (const [key, group] of Object.entries(groups)) {
    const [curveName, tsStr] = key.split("__");
    const hourTs = Number(tsStr);
    const hasQuarterly = group.some((r) =>
      [15, 30, 45].includes(new Date(r.dateTimeOffset).getMinutes())
    );
    const value = hasQuarterly
      ? group.reduce(
          (s, r) => s + (typeof r.value === "number" ? r.value : 0),
          0
        )
      : (() => {
          const found = group.find((r) => typeof r.value === "number");
          return found ? found.value : null;
        })();
    out.push({
      ...group[0],
      curveName,
      Date: new Date(hourTs).toISOString(),
      value,
    });
  }
  out.sort(
    (a, b) =>
      new Date(a.Date).getTime() - new Date(b.Date).getTime() ||
      a.curveName.localeCompare(b.curveName)
  );
  return out;
};

const style = {
  alignment: {
    horizontal: ["center"],
  },
  font: {
    bold: true,
  },
  fill: {
    type: "pattern",
    patternType: "solid",
    bgColor: "#99CC00",
    fgColor: "#99CC00",
  },
};

const getTimeseriesData = (data: any, curves: any) =>
  R.ifElse(
    (x) => R.isEmpty(x) || R.isNil(x),
    () => [],
    R.pipe<any, any, any, any, any, any>(
      R.toPairs,
      R.map(([curveName, val]) => {
        return {
          curveName,
          data: R.map((row) => ({ ...row, curveName }), val.timeseries),
        };
      }),
      R.reject((row: any) => !R.contains(row.curveName, curves || [])),
      R.map(R.prop("data")),
      R.flatten
    )
  )(data);

export const timeseriesHeaders = ({
  curves,
  granularity,
  sheet,
}: {
  curves: any;
  granularity: any;
  sheet: any;
}) => {
  sheet.row(1).freeze();
  sheet.row(2).freeze();
  sheet
    .cell(1, 1)
    .string("Granularity:")
    .style(style);
  sheet
    .cell(1, 2)
    .string(getGranName(granularity))
    .style(style);

  sheet
    .cell(2, 1)
    .string("Date")
    .style(style);
  return curves.map((title: any, i: number) =>
    sheet
      .cell(2, R.add(i, 2))
      .string(getCurveName(title))
      .style(style)
  );
};

const getDates = (data: any) =>
  data.reduce(
    (acc: any, cv: any) =>
      cv.timeseries.length > acc.length ? cv.timeseries : acc,
    []
  );

type BodyType = {
  curves: any;
  sheet: any;
  data: any;
  gran: any;
  tideDate?: Date | string | number;
};
export const timeseriesBody = ({ curves, sheet, data, gran }: BodyType) => {
  const maxDatesList = getDates([
    data.net,
    data.settlement,
    data.spread,
    data.unbalance,
  ]);
  const timeseriesData = getTimeseriesData(data, curves);

  const allDates =
    gran === "daily"
      ? getDailyDates(maxDatesList)
      : gran === "hourly"
      ? getHourlyDates(timeseriesData)
      : getQuarterlyDates(timeseriesData);

  const uniformData =
    gran === "daily"
      ? getDailyInfo(timeseriesData)
      : gran === "hourly"
      ? getHourlyInfo(timeseriesData)
      : getQuarterlyInfo(timeseriesData);

  return R.pipe(
    R.map((rd) => R.filter(R.propEq("Date", rd), uniformData)),
    R.toPairs,
    (x) =>
      x.map(([row, arr]: any, ri: number) => {
        const dateVal = R.equals(gran, "daily")
          ? allDates[row]
          : format(new Date(allDates[row]), "dd-MM-yyyy'T'HH:mm:ss");
        sheet.cell(R.add(ri, 3), 1).string(dateVal);
        return curves.map((col: any, ci: number) => {
          const d = R.pipe<any, any, any>(
            R.filter(
              (x: any) =>
                R.equals(x.curveName, col) && R.equals(x.Date, allDates[row])
            ),
            R.pathOr(null, [0, "value"])
          )(arr);
          return R.isNil(d)
            ? sheet
            : sheet.cell(R.add(ri, 3), R.add(ci, 2)).number(d);
        });
      })
  )(allDates);
};
